import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { FileCheck, Loader2, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

type FileDropzoneProps = {
  accept?: string;
  title?: string;
  hint?: string;
  fileName?: string;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  clearable?: boolean;
  error?: string;
  onFileSelected: (file: File) => void;
  onClear?: () => void;
  children?: ReactNode;
};

export default function FileDropzone({
  accept = "",
  title = "Upload a file",
  hint = "Drag and drop or click to browse",
  fileName = "",
  loading = false,
  loadingText = "Processing…",
  disabled = false,
  clearable = false,
  error = "",
  onFileSelected,
  onClear,
  children,
}: FileDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blocked = disabled || loading;

  const openPicker = () => {
    if (blocked) return;
    inputRef.current?.click();
  };

  const takeFile = (file: File | undefined) => {
    if (!file || blocked) return;
    onFileSelected(file);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openPicker();
  };

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={blocked ? -1 : 0}
        aria-disabled={blocked}
        onClick={openPicker}
        onKeyDown={onKeyDown}
        onDragOver={(event) => {
          event.preventDefault();
          if (!blocked) setDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          takeFile(event.dataTransfer.files?.[0]);
        }}
        className={cn(
          "relative cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all duration-200 md:p-8",
          blocked && "cursor-not-allowed border-white/10 bg-black/10 opacity-60",
          !blocked && error && "border-red-500/50 bg-red-500/5 hover:border-red-500/70",
          !blocked && !error && dragging && "border-[#D4FF00] bg-[#D4FF00]/10 shadow-[0_0_0_1px_rgba(212,255,0,0.25)]",
          !blocked && !error && !dragging && fileName && "border-[#D4FF00]/50 bg-[#D4FF00]/5 hover:border-[#D4FF00]/70 hover:bg-[#D4FF00]/10",
          !blocked && !error && !dragging && !fileName && "border-white/15 bg-black/20 hover:border-[#D4FF00]/40 hover:bg-[#D4FF00]/5",
        )}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          disabled={blocked}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            takeFile(file);
          }}
        />

        {loading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-[#D4FF00]" aria-hidden="true" />
            <p className="text-sm font-medium text-[#D4FF00]">{loadingText}</p>
          </div>
        ) : fileName ? (
          <div className="flex flex-col items-center gap-2">
            <FileCheck className="h-8 w-8 text-[#D4FF00]" aria-hidden="true" />
            <p className="text-sm font-medium text-[#D4FF00]">{fileName}</p>
            <p className="text-xs text-white/50">Click or drop to replace</p>
            {clearable && onClear && (
              <button
                type="button"
                className="mt-1 text-xs text-white/50 underline-offset-2 hover:text-red-400 hover:underline"
                onClick={(event) => {
                  event.stopPropagation();
                  onClear();
                }}
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload
              className={cn("h-8 w-8 transition-colors", dragging ? "text-[#D4FF00]" : "text-white/40")}
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-white">{title}</p>
            <p className="text-xs text-white/50">{hint}</p>
            {dragging && <p className="text-xs font-medium text-[#D4FF00]">Drop file here</p>}
          </div>
        )}
      </div>

      {children}

      {error && (
        <p className="text-sm text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
